
import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

// Sample data for session completion status
const sessionStatusData = [
  { name: 'Completed', value: 78, color: '#10b981' },
  { name: 'Cancelled', value: 12, color: '#ef4444' },
  { name: 'Rescheduled', value: 10, color: '#f59e0b' },
];

// Peak booking times data
const peakBookingData = [
  { day: 'Monday', morning: 8, afternoon: 12, evening: 5 },
  { day: 'Tuesday', morning: 12, afternoon: 15, evening: 8 },
  { day: 'Wednesday', morning: 10, afternoon: 14, evening: 6 },
  { day: 'Thursday', morning: 7, afternoon: 16, evening: 9 },
  { day: 'Friday', morning: 15, afternoon: 18, evening: 5 },
  { day: 'Saturday', morning: 20, afternoon: 8, evening: 2 },
  { day: 'Sunday', morning: 3, afternoon: 5, evening: 2 },
];

// Calculate busiest times
const flatBookingData = peakBookingData.flatMap(day => [
  { dayTime: `${day.day} Morning`, count: day.morning },
  { dayTime: `${day.day} Afternoon`, count: day.afternoon },
  { dayTime: `${day.day} Evening`, count: day.evening },
]);

// Sort by count and get top 3
const busiestTimes = [...flatBookingData].sort((a, b) => b.count - a.count).slice(0, 3);

const TimeSessionInsights: React.FC = () => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div>
        <h3 className="text-sm font-medium mb-3">Session Completion Status</h3>
        <div className="h-[250px] flex items-center justify-center">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={sessionStatusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {sessionStatusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `${value} sessions`} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 text-center text-sm">
          <div className="font-medium">Average Sessions per Patient: <span className="text-primary">3.8</span></div>
          <div className="text-muted-foreground">Most patients complete between 3-5 sessions</div>
        </div>
      </div>
      
      <div>
        <h3 className="text-sm font-medium mb-3">Peak Booking Times</h3>
        <div className="grid grid-cols-7 gap-1 h-[250px]">
          {peakBookingData.map((day, dayIndex) => (
            <div key={dayIndex} className="flex flex-col h-full">
              <div className="text-xs text-center mb-1 font-medium">{day.day.substring(0, 3)}</div>
              <div className="flex-1 flex flex-col gap-1">
                <div 
                  className="flex-1 bg-red-100 rounded-sm relative overflow-hidden" 
                  style={{ opacity: day.morning / 20 }}
                >
                  <div className="absolute inset-0 flex items-center justify-center text-[8px] text-red-800 font-bold">
                    {day.morning}
                  </div>
                </div>
                <div 
                  className="flex-1 bg-amber-100 rounded-sm relative overflow-hidden" 
                  style={{ opacity: day.afternoon / 20 }}
                >
                  <div className="absolute inset-0 flex items-center justify-center text-[8px] text-amber-800 font-bold">
                    {day.afternoon}
                  </div>
                </div>
                <div 
                  className="flex-1 bg-blue-100 rounded-sm relative overflow-hidden" 
                  style={{ opacity: day.evening / 20 }}
                >
                  <div className="absolute inset-0 flex items-center justify-center text-[8px] text-blue-800 font-bold">
                    {day.evening}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-2 grid grid-cols-3 text-xs text-center">
          <div><span className="inline-block w-2 h-2 bg-red-200 rounded-full mr-1"></span> Morning</div>
          <div><span className="inline-block w-2 h-2 bg-amber-200 rounded-full mr-1"></span> Afternoon</div>
          <div><span className="inline-block w-2 h-2 bg-blue-200 rounded-full mr-1"></span> Evening</div>
        </div>
        <div className="mt-4">
          <h4 className="text-xs font-medium mb-2">Busiest Times:</h4>
          <div className="space-y-2">
            {busiestTimes.map((time, index) => (
              <div key={index} className="flex items-center">
                <div className={`w-1 h-4 bg-primary mr-2 rounded-full`}></div>
                <div className="text-sm">{time.dayTime}: <span className="font-medium">{time.count} bookings</span></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeSessionInsights;
